
import { Component, Vue } from 'vue-property-decorator';
import Container from 'typedi'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'
import GtrStorage from '@/modules/common/services/storage.service'
import {mapActions, mapState} from 'vuex'
import {CoursePayload, LmsSettings} from '@/modules/learning/learning.model'

@Component({
  computed: {
    ...mapState('learning', ['course', 'lms_settings'])
  },
  methods: {
    ...mapActions('learning', ['getCourse', 'getChapters', 'getTableOfContents'])
  }
})
export default class LearningLayout extends Vue {
  getCourse!: (payload: CoursePayload) => Promise<any>
  getChapters!: (payload: CoursePayload) => Promise<any>
  getTableOfContents!: (payload: CoursePayload) => Promise<any>

  lms_settings!: LmsSettings

  get dynamicBackground () {
    return this.$route.name === 'learning.dashboard' ? 'primary-color-bg' : 'light-grey-bg'
  }

  async mounted () {
    try {
      const course_uuid = this.$route.params.course_uuid
      this.$store.dispatch('common/showLoader', { value: true })
      if (course_uuid && Container.get(GtrStorage).getItem(`${course_uuid}_registration_access_token`)) {
        await this.$store.dispatch('auth/getParticipant', { event_identifier: course_uuid })
        await this.getCourse({ course_uuid })
        await this.getChapters({ course_uuid })
        await this.getTableOfContents({ course_uuid })
        if (this.lms_settings?.design) {
          this.setLMSDesignColors()
        }
      }
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    } finally {
      this.$store.dispatch('common/hideLoader')
    }
  }

  setLMSDesignColors () {
    this.setPrimaryColor()
    this.setSecondaryColor()
  }

  setPrimaryColor () {
    const primaryColor = this.lms_settings.design.primary_color
    const { h, s, l } = this.convertHexToHsl(primaryColor)
    const primaryAnalogousColor = `hsl(${(h + 30) % 360}, ${s}%, ${l}%)`
    document.documentElement.style.setProperty('--primary-color', primaryColor)
    document.documentElement.style.setProperty('--primary-analogous-color', primaryAnalogousColor)
    if (l > 45) {
      document.documentElement.style.setProperty('--primary-text-color', '#262626')
    }
  }

  setSecondaryColor () {
    const secondaryColor = this.lms_settings.design.secondary_color
    const { h, s, l } = this.convertHexToHsl(secondaryColor)
    const secondaryAnalogousColor = `hsl(${(h + 30) % 360}, ${s}%, ${l}%)`
    document.documentElement.style.setProperty('--secondary-color', secondaryColor)
    document.documentElement.style.setProperty('--secondary-analogous-color', secondaryAnalogousColor)
    if (l > 45) {
      document.documentElement.style.setProperty('--secondary-text-color', '#565656')
    }
  }

  convertHexToHsl (hex: string) {
    const { r, g, b } = this.hexToRgb(hex)
    return this.rgbToHsl(r, g, b)
  }

  hexToRgb (hex: string) {
    const r = parseInt(hex.substring(1, 3), 16)
    const g = parseInt(hex.substring(3, 5), 16)
    const b = parseInt(hex.substring(5, 7), 16)
    return { r, g, b }
  }

  rgbToHsl (r, g, b) {
    r /= 255
    g /= 255
    b /= 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    // eslint-disable-next-line prefer-const
    let h, s, l = (max + min) / 2

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0)
          break
        case g:
          h = (b - r) / d + 2
          break
        case b:
          h = (r - g) / d + 4
          break
    }
      h /= 6
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    }
  }
}
